<template>
  <el-dialog
    :title="$t('billingPage.PostageRechargeUploadTitle')"
    :visible.sync="dialogVisible"
    width="560px"
    destroy-on-close
    append-to-body
    :before-close="hide"
  >
    <el-form ref="form" :model="form" :rules="rules" label-position="top">
      <el-form-item :label="$t('billingPage.PostageRechargeInvoiceNo')" prop="invoiceNumber">
        <BjInput
          v-model="form.invoiceNumber"
          :maxlength="MAX_LENGTH.DEFAULT"
          :placeholder="$t('billingPage.PostageRechargeInvoicePlaceholder')"
        />
      </el-form-item>
      <el-form-item :label="$t('billingPage.PostageRechargeIssueDate')" prop="issueDate">
        <el-date-picker
          v-model="form.issueDate"
          style="width: 100%"
          type="date"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          :placeholder="$t('billingPage.PostageRechargeSelectIssueDate')"
        />
      </el-form-item>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item :label="$t('billingPage.PostageRechargeCarrier')" prop="logisticsCompany">
            <BjSelectLov
              v-model="form.logisticsCompany"
              :option-list="companyList"
              :placeholder="$t('billingPage.PostageRechargeSelectPartner')"
              @change="handleCarrierChange"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('billingPage.PostageRechargeFinancialAmount')" prop="financialAmount">
            <BjInputMoney
              v-model="form.financialAmount"
              :placeholder="$t('billingPage.PostageRechargeFinancialAmountPlaceholder')"
              :not-auto-zero="true"
            >
              <template slot="prepend">$</template>
            </BjInputMoney>
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item
        :label="$t(`billingPage.PostageRechargeUpload${primaryFileFormat}File`)"
      >
        <el-upload
          ref="upload"
          class="invoice-upload"
          drag
          action=""
          :accept="getPrimaryUploadOptions().acceptTypes.join(',')"
          :limit="1"
          :file-list="fileList"
          :auto-upload="false"
          :on-change="handleFileChange"
          :on-exceed="handleFileExceed"
          :on-remove="handleFileRemove"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">{{ $t('billingPage.PostageRechargeDropFile') }}</div>
          <div slot="tip" class="el-upload__tip">
            {{ $t(`billingPage.PostageRechargeUpload${primaryFileFormat}Tip`) }}
          </div>
        </el-upload>
      </el-form-item>
      <el-form-item v-if="requiresPdfAndCsv" :label="$t('billingPage.PostageRechargeUploadCsvFile')">
        <el-upload
          ref="csvUpload"
          class="invoice-upload"
          drag
          action=""
          accept=".csv"
          :limit="1"
          :file-list="csvFileList"
          :auto-upload="false"
          :on-change="handleCsvFileChange"
          :on-exceed="handleCsvFileExceed"
          :on-remove="handleCsvFileRemove"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">{{ $t('billingPage.PostageRechargeDropFile') }}</div>
          <div slot="tip" class="el-upload__tip">
            {{ $t('billingPage.PostageRechargeUploadCsvTip') }}
          </div>
        </el-upload>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="hide">{{ $t('cancelText') }}</el-button>
      <el-button type="primary" :loading="confirmLoading" @click="confirm">
        {{ $t('billingPage.PostageRechargeUpload') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { billApi } from '@/api';
import { getInvoiceWeek } from '../invoice-week';

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const PDF_AND_CSV_CARRIERS = ['HUNT', 'TNT'];

export default {
  name: 'PostageRechargeUploadInvoiceDialog',
  props: {
    companyList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dialogVisible: false,
      confirmLoading: false,
      file: null,
      csvFile: null,
      fileList: [],
      csvFileList: [],
      form: {
        invoiceNumber: '',
        issueDate: '',
        logisticsCompany: '',
        financialAmount: ''
      },
      rules: {
        invoiceNumber: [{ required: true, message: this.$t('inputText'), trigger: 'blur' }],
        issueDate: [{ required: true, message: this.$t('chooseText'), trigger: 'change' }],
        logisticsCompany: [{ required: true, message: this.$t('chooseText'), trigger: 'change' }],
        financialAmount: [{ required: true, message: this.$t('inputText'), trigger: 'change' }]
      }
    };
  },
  computed: {
    requiresPdfAndCsv() {
      return PDF_AND_CSV_CARRIERS.includes(this.form.logisticsCompany);
    },
    primaryFileFormat() {
      if (this.requiresPdfAndCsv || this.form.logisticsCompany === 'DIFECN') return 'Pdf';
      return this.form.logisticsCompany === 'IMI' ? 'CsvXlsx' : 'Csv';
    }
  },
  methods: {
    show() {
      this.dialogVisible = true;
      this.resetForm();
    },
    hide() {
      this.dialogVisible = false;
      this.resetForm();
    },
    resetForm() {
      this.confirmLoading = false;
      this.file = null;
      this.csvFile = null;
      this.fileList = [];
      this.csvFileList = [];
      this.form = {
        invoiceNumber: '',
        issueDate: '',
        logisticsCompany: '',
        financialAmount: ''
      };
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate();
        if (this.$refs.upload) this.$refs.upload.clearFiles();
        if (this.$refs.csvUpload) this.$refs.csvUpload.clearFiles();
      });
    },
    handleCarrierChange() {
      this.file = null;
      this.csvFile = null;
      this.fileList = [];
      this.csvFileList = [];
      this.$nextTick(() => {
        if (this.$refs.upload) this.$refs.upload.clearFiles();
        if (this.$refs.csvUpload) this.$refs.csvUpload.clearFiles();
      });
    },
    setUploadFile(rawFile, uploadFile, options) {
      if (!rawFile) return false;
      const fileName = rawFile.name || '';
      const suffixIndex = fileName.lastIndexOf('.');
      const suffix = suffixIndex > -1 ? fileName.slice(suffixIndex).toLowerCase() : '';
      if (!options.acceptTypes.includes(suffix)) {
        this.$message.warning(this.$t(options.fileTypeMessage));
        this.clearUploadFile(options);
        return false;
      }
      if (rawFile.size > MAX_FILE_SIZE) {
        this.$message.warning(this.$t('billingPage.PostageRechargeUploadSizeTip'));
        this.clearUploadFile(options);
        return false;
      }
      this[options.fileKey] = rawFile;
      this[options.fileListKey] = [uploadFile || {
        name: rawFile.name,
        uid: rawFile.uid || Date.now(),
        status: 'ready',
        raw: rawFile
      }];
      return true;
    },
    clearUploadFile(options) {
      const upload = this.$refs[options.refName];
      if (upload) upload.clearFiles();
      this[options.fileKey] = null;
      this[options.fileListKey] = [];
    },
    getPrimaryUploadOptions() {
      return {
        acceptTypes: this.primaryFileFormat === 'Pdf'
          ? ['.pdf']
          : this.primaryFileFormat === 'CsvXlsx' ? ['.csv', '.xlsx'] : ['.csv'],
        fileTypeMessage: `billingPage.PostageRechargeUpload${this.primaryFileFormat}FileTypeTip`,
        refName: 'upload',
        fileKey: 'file',
        fileListKey: 'fileList'
      };
    },
    getCsvUploadOptions() {
      return {
        acceptTypes: ['.csv'],
        fileTypeMessage: 'billingPage.PostageRechargeUploadCsvFileTypeTip',
        refName: 'csvUpload',
        fileKey: 'csvFile',
        fileListKey: 'csvFileList'
      };
    },
    handleFileChange(file) {
      const rawFile = file.raw;
      this.setUploadFile(rawFile, file, this.getPrimaryUploadOptions());
    },
    handleFileExceed(files) {
      const rawFile = files && files[0];
      this.$refs.upload.clearFiles();
      this.setUploadFile(rawFile, null, this.getPrimaryUploadOptions());
    },
    handleFileRemove() {
      this.file = null;
      this.fileList = [];
    },
    handleCsvFileChange(file) {
      this.setUploadFile(file.raw, file, this.getCsvUploadOptions());
    },
    handleCsvFileExceed(files) {
      const rawFile = files && files[0];
      this.$refs.csvUpload.clearFiles();
      this.setUploadFile(rawFile, null, this.getCsvUploadOptions());
    },
    handleCsvFileRemove() {
      this.csvFile = null;
      this.csvFileList = [];
    },
    confirm() {
      this.$refs.form.validate(valid => {
        if (!valid) return;
        if (!this.file) {
          const requiredMessage = `billingPage.PostageRechargeUpload${this.primaryFileFormat}FileRequired`;
          this.$message.warning(this.$t(requiredMessage));
          return;
        }
        if (this.requiresPdfAndCsv && !this.csvFile) {
          this.$message.warning(this.$t('billingPage.PostageRechargeUploadCsvFileRequired'));
          return;
        }
        const invoiceWeek = getInvoiceWeek();
        const formData = new FormData();
        formData.append('file', this.file);
        if (this.requiresPdfAndCsv) formData.append('csvFile', this.csvFile);
        this.confirmLoading = true;
        billApi.uploadPostageRechargeInvoice({
          BjBody: formData,
          BjQuery: {
            invoiceNumber: this.form.invoiceNumber,
            carrierName: this.form.logisticsCompany,
            week: invoiceWeek.week,
            year: invoiceWeek.year,
            issueDate: this.form.issueDate,
            financialAmount: this.form.financialAmount
          }
        }).then(res => {
          if (res.code == 200 || !res.failed) {
            this.msgSuccess(this.$t('toast.operationSuccessText'));
            this.hide();
            this.$emit('refresh');
          }
        }).finally(() => {
          this.confirmLoading = false;
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.invoice-upload {
  width: 100%;

  ::v-deep .el-upload,
  ::v-deep .el-upload-dragger {
    width: 100%;
  }
}
</style>
